import { Alert, Paper, Title } from "@mantine/core";
import * as _ from "lodash-es";
import { useCallback, useMemo } from "react";
import { Route, Routes, useParams } from "react-router";
import type { ApiResponse } from "../../api";
import { useNav } from "../../hooks/useNav";
import { useProject } from "../../hooks/useProject";
import { useSiteCategories } from "../../hooks/useSiteCategories";
import { useSystemUsers } from "../../hooks/useSystemUsers";
import { AlertPresets } from "../../mantine/mantinePresets";
import { useToast } from "../../mantine/notifications/useToast";
import type {
  ExtendedUser,
  Priority,
  Todo,
  TodoStatus,
  User
} from "../../utils/backend-types";
import { getErrorText } from "../../utils/get-error-text";
import { hasPremiumSite } from "../../utils/hasPremiumSite";
import { PageContent } from "../BuildingBlocks/Layout/PageContent/PageContent";
import { NoDataMessage } from "../BuildingBlocks/NoDataMessage/NoDataMessage";
import { LoadOrError } from "../LoadOrError/LoadOrError";
import { useTodos } from "./hooks/useTodos";
import { SingleTodo } from "./SingleTodo";
import type { TodoUser } from "./Todos.types";
import { Mode } from "./Todos.types";
import { TodoTable } from "./TodoTable/TodoTable";
import { getDefaultTodoTab } from "./utils/getDefaultTodoTab";

interface TodosProps {
  todos: Array<Todo>;
  users: Array<ExtendedUser>;
  systemUsers: Array<User>;
  variantId: number;
  projectId: string;
  mode: Mode.Universal | Mode.Onboarding;
  updateTodoResponsible: (todo: Todo) => void;
  updateTodoStatus: (todo: Todo) => void;
  updateTodoPriority: (todo: Todo) => void;
  updateTodoDueDate: (todo: Todo) => void;
  updateTodoOverdue: (todo: Todo) => void;
  updateTodoPerson: (todoId: number, personId: number | null) => void;
  removeTodo: (todo: Todo) => Promise<ApiResponse>;
  removeTodos: (todos: Array<Todo>) => Promise<Array<ApiResponse>>;
  updateDocumentProvided: (todoId: number) => void;
}

function Todos({
  todos,
  users,
  systemUsers,
  variantId,
  projectId,
  mode,
  updateTodoResponsible,
  updateTodoPriority,
  updateTodoStatus,
  updateTodoDueDate,
  updateTodoOverdue,
  updateTodoPerson,
  removeTodo,
  removeTodos,
  updateDocumentProvided
}: TodosProps) {
  const { siteCategories } = useSiteCategories(projectId);
  const showPriorityColumn = hasPremiumSite(siteCategories);

  const showTodoStatus =
    siteCategories &&
    siteCategories.every((site) => site.is_partial_feedin_site);

  const navigate = useNav();

  const toggleTodoModal = useCallback(
    (todo: Todo | false) => {
      if (todo) {
        navigate(`./${todo.id}/${getDefaultTodoTab(todo)}`);
      } else {
        navigate(".");
      }
    },
    [navigate]
  );

  const handleChangeStatus = useCallback(
    (status: TodoStatus, todoId: number) => {
      const todo = todos.find((todo) => todo.id === todoId);

      if (todo) {
        updateTodoStatus({
          ...todo,
          status: status
        });
      }
    },
    [todos, updateTodoStatus]
  );

  const handleChangePriority = useCallback(
    (priorityValue: Priority, todoId: number) => {
      const todo = todos.find((todo) => todo.id === todoId);

      if (todo) {
        updateTodoPriority({
          ...todo,
          priority: priorityValue
        });
      }
    },
    [todos, updateTodoPriority]
  );

  const handleChangeResponsible = useCallback(
    (responsibleId: number | null, todoId: number) => {
      const todo = todos.find((todo) => todo.id === todoId);

      if (todo) {
        updateTodoResponsible({
          ...todo,
          responsible: responsibleId
        });
      }
    },
    [todos, updateTodoResponsible]
  );

  const handleChangeDueDate = useCallback(
    (todoId: number, dueDate: string | null) => {
      const todo = todos.find((todo) => todo.id === todoId);

      if (todo && dueDate) {
        updateTodoDueDate({
          ...todo,
          dueDate: dueDate
        });
      }
    },
    [todos, updateTodoDueDate]
  );

  const handleChangePerson = useCallback(
    (todoId: number, person: number | null) => {
      const todo = todos.find((todo) => todo.id === todoId);
      if (todo) {
        updateTodoPerson(todoId, person);
      }
    },
    [todos, updateTodoPerson]
  );

  const handleChangeOverdue = useCallback(
    (todoId: number, overdue: boolean) => {
      const todo = todos.find((todo) => todo.id === todoId);

      if (todo) {
        updateTodoOverdue({
          ...todo,
          overdue: overdue
        });
      }
    },
    [todos, updateTodoOverdue]
  );

  // It may happen that a system user is also a project manager
  const uniqueUsersWithSystemUsers = useMemo(() => {
    const markedSystemUsers = systemUsers.map<TodoUser>((user) => {
      return { ...user, isSystemUser: true };
    });

    return _.uniqBy<TodoUser>(users.concat(markedSystemUsers), "id");
  }, [users, systemUsers]);

  // in case not saved yet into redux
  if (!todos) {
    return null;
  }

  const ElementSingleTodo = (
    <SingleTodo
      mode={mode}
      projectId={projectId}
      showTodoStatus={showTodoStatus}
      todos={todos}
      users={uniqueUsersWithSystemUsers}
      onChangeDueDate={handleChangeDueDate}
      onChangeOverdue={handleChangeOverdue}
      onChangePerson={handleChangePerson}
      onChangePriority={handleChangePriority}
      onChangeResponsible={handleChangeResponsible}
      onChangeStatus={handleChangeStatus}
      onChangeTodoStatus={updateDocumentProvided}
      onToggleTodoModal={() => toggleTodoModal(false)}
    />
  );

  return (
    <PageContent className="Todos">
      <Paper className="Todos">
        <Title variant="paper-header">
          {mode === Mode.Onboarding ? "Onboarding" : "Aufgaben"}
        </Title>

        {todos.length > 0 ? (
          <TodoTable
            mode={mode}
            removeTodo={removeTodo}
            removeTodos={removeTodos}
            showPersonColumn
            showPriorityColumn={showPriorityColumn}
            showTodoStatusColumn={showTodoStatus}
            todos={todos}
            users={uniqueUsersWithSystemUsers}
            variantId={variantId}
            onChangeDueDate={handleChangeDueDate}
            onChangeOverdue={handleChangeOverdue}
            onChangePriority={handleChangePriority}
            onChangeResponsible={handleChangeResponsible}
            onChangeStatus={handleChangeStatus}
            onChangeTodoPerson={handleChangePerson}
            onChangeTodoStatus={updateDocumentProvided}
            onClickRow={toggleTodoModal}
          />
        ) : (
          <NoDataMessage message="Es gibt noch keine Aufgaben in diesem Projekt" />
        )}

        <Routes>
          <Route element={ElementSingleTodo} path=":todoId">
            <Route element={ElementSingleTodo} index />
            <Route element={ElementSingleTodo} path="*" />
          </Route>
        </Routes>
      </Paper>
    </PageContent>
  );
}
interface TodosDataLoaderProps {
  mode: Mode.Universal | Mode.Onboarding;
  variantId: number;
  projectId: string;
}

function TodosDataLoader({ mode, variantId, projectId }: TodosDataLoaderProps) {
  const { showErrorToast } = useToast();
  const {
    todos,
    isLoading: todosLoading,
    error: todosError,
    updateTodoResponsible,
    updateTodoStatus,
    updateTodoPriority,
    updateTodoDueDate,
    updateTodoOverdue,
    updateTodoPerson,
    removeTodo,
    removeTodos,
    updateDocumentProvided
  } = useTodos({
    mode,
    variantId,
    onUpdateError: (error) => {
      showErrorToast(error);
    }
  });
  const {
    isLoading: systemUsersLoading,
    error: systemUsersError,
    data: systemUsers
  } = useSystemUsers();

  const {
    project,
    isLoading: projectLoading,
    error: projectError
  } = useProject(projectId);

  const loading = todosLoading || systemUsersLoading || projectLoading;

  const error = todosError || systemUsersError || projectError;

  const users = project ? project.managers : null;
  return (
    <LoadOrError error={error} loading={loading}>
      {todos && users && systemUsers && (
        <Todos
          mode={mode}
          projectId={projectId}
          removeTodo={removeTodo}
          removeTodos={removeTodos}
          systemUsers={systemUsers}
          todos={todos}
          updateDocumentProvided={updateDocumentProvided}
          updateTodoDueDate={updateTodoDueDate}
          updateTodoOverdue={updateTodoOverdue}
          updateTodoPerson={updateTodoPerson}
          updateTodoPriority={updateTodoPriority}
          updateTodoResponsible={updateTodoResponsible}
          updateTodoStatus={updateTodoStatus}
          users={users}
          variantId={variantId}
        />
      )}
    </LoadOrError>
  );
}

function TodosDataLoaderAndProjectId(
  props: Omit<TodosDataLoaderProps, "projectId">
) {
  const { projectId } = useParams();

  // this should never happen unless Todos is used incorrectly from a wrong route
  if (!projectId) {
    return <Alert color={AlertPresets.error.color}>{getErrorText(null)}</Alert>;
  }

  return <TodosDataLoader {...props} projectId={projectId} />;
}

export {
  TodosDataLoaderAndProjectId as Todos,
  TodosDataLoaderProps as TodosProps,
  TodosDataLoader as TodosWithoutProjectIdInRoute
};
